import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Moment } from "moment";
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_END_HOUR,
  CALENDAR_GRID_MAX_HEIGHT,
  CALENDAR_HOUR_HEIGHT,
  CALENDAR_SCROLL_TO_HOUR,
  CALENDAR_SLOT_MINUTES,
  CALENDAR_START_HOUR,
  CALENDAR_TIME_SLOTS,
  JOB_DRAG_SNAP_MINUTES,
  JOB_DRAG_THRESHOLD_PX,
} from "../../../../constants";
import { JobDragMode } from "../../../../enums";
import { JobModel } from "../../../../models";
import { Job, JobScheduleChange } from "../../../../types";
import { theme } from "../../../../utils";
import { CalendarDayHeader } from "./CalendarDayHeader";
import { JobCard } from "./JobCard";

type JobDragState = {
  job: Job;
  mode: JobDragMode;
  startX: number;
  startY: number;
  columnWidth: number;
  dayIndex: number;
  deltaX: number;
  deltaY: number;
};

const hasDragMoved = (drag: JobDragState) =>
  Math.abs(drag.deltaX) > JOB_DRAG_THRESHOLD_PX ||
  Math.abs(drag.deltaY) > JOB_DRAG_THRESHOLD_PX;

const getGridColumns = (dayCount: number) =>
  `56px repeat(${dayCount}, minmax(120px, 1fr))`;
const MINUTE_HEIGHT = CALENDAR_HOUR_HEIGHT / 60;
const SLOT_HEIGHT = CALENDAR_SLOT_MINUTES * MINUTE_HEIGHT;
const DAY_HEIGHT =
  (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * CALENDAR_HOUR_HEIGHT;

type JobsWeekViewProps = {
  days: Moment[];
  today: string;
  jobs: Job[];
  conflictJobIds: Set<number>;
  onSlotClick: (date: Moment, startTime: string) => void;
  onJobClick: (job: Job) => void;
  onDayClick?: (day: Moment) => void;
  onJobMove?: (job: Job, changes: JobScheduleChange) => void;
};

export const JobsWeekView = ({
  days,
  today,
  jobs,
  conflictJobIds,
  onSlotClick,
  onJobClick,
  onDayClick,
  onJobMove,
}: JobsWeekViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<JobDragState | null>(null);
  const justDraggedRef = useRef(false);
  const [drag, setDrag] = useState<JobDragState | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDragEnabled = !!onJobMove && !isMobile;
  const gridColumns = getGridColumns(days.length);

  const updateDrag = (nextDrag: JobDragState | null) => {
    dragRef.current = nextDrag;
    setDrag(nextDrag);
  };

  const getDragPreview = (current: JobDragState): JobScheduleChange => {
    const { job } = current;
    const dayEndMinutes = CALENDAR_END_HOUR * 60;
    const startMinutes = JobModel.timeToMinutes(job.startTime);
    const deltaMinutes =
      Math.round(current.deltaY / MINUTE_HEIGHT / JOB_DRAG_SNAP_MINUTES) *
      JOB_DRAG_SNAP_MINUTES;

    if (current.mode === JobDragMode.Resize) {
      return {
        date: job.date,
        startTime: job.startTime,
        durationMinutes: Math.min(
          Math.max(job.durationMinutes + deltaMinutes, JOB_DRAG_SNAP_MINUTES),
          dayEndMinutes - startMinutes,
        ),
      };
    }

    const dayIndex = Math.min(
      Math.max(
        current.dayIndex + Math.round(current.deltaX / current.columnWidth),
        0,
      ),
      days.length - 1,
    );
    const nextStartMinutes = Math.min(
      Math.max(startMinutes + deltaMinutes, CALENDAR_START_HOUR * 60),
      dayEndMinutes - job.durationMinutes,
    );

    return {
      date: days[dayIndex].format(CALENDAR_DATE_FORMAT),
      startTime: JobModel.minutesToTime(nextStartMinutes),
      durationMinutes: job.durationMinutes,
    };
  };

  const displayJobs = useMemo(() => {
    if (!drag || !hasDragMoved(drag)) return jobs;

    const preview = getDragPreview(drag);

    return jobs.map((job) =>
      job.id === drag.job.id ? { ...job, ...preview } : job,
    );
  }, [jobs, drag, days]);

  const handlePointerDown = (
    event: React.PointerEvent<HTMLElement>,
    job: Job,
    mode: JobDragMode,
  ) => {
    if (!isDragEnabled || event.button !== 0 || event.pointerType === "touch")
      return;

    const column = event.currentTarget.closest<HTMLElement>("[data-day-index]");
    if (!column) return;

    event.preventDefault();
    event.stopPropagation();
    updateDrag({
      job,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      columnWidth: column.offsetWidth,
      dayIndex: Number(column.dataset.dayIndex),
      deltaX: 0,
      deltaY: 0,
    });
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop =
        (CALENDAR_SCROLL_TO_HOUR - CALENDAR_START_HOUR) * CALENDAR_HOUR_HEIGHT;
    }
  }, []);

  useEffect(() => {
    if (!drag) return;

    const handlePointerMove = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;

      updateDrag({
        ...current,
        deltaX: event.clientX - current.startX,
        deltaY: event.clientY - current.startY,
      });
    };

    const handlePointerUp = () => {
      const current = dragRef.current;
      updateDrag(null);
      if (!current || !hasDragMoved(current)) return;

      justDraggedRef.current = true;
      setTimeout(() => {
        justDraggedRef.current = false;
      });

      const preview = getDragPreview(current);
      const { job } = current;

      if (
        preview.date !== job.date ||
        preview.startTime !== job.startTime ||
        preview.durationMinutes !== job.durationMinutes
      ) {
        onJobMove?.(job, preview);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [!!drag]);

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ minWidth: 900 }}>
        <CalendarDayHeader
          days={days}
          today={today}
          gridTemplateColumns={gridColumns}
          onDayClick={onDayClick}
        />

        <Box
          ref={scrollRef}
          sx={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            maxHeight: CALENDAR_GRID_MAX_HEIGHT,
            overflowY: "auto",
            pt: 1,
            userSelect: drag ? "none" : undefined,
          }}
        >
          <Box>
            {CALENDAR_TIME_SLOTS.filter((_, index) => index % 2 === 0).map(
              (slot) => (
                <Typography
                  key={slot.value}
                  sx={{
                    height: CALENDAR_HOUR_HEIGHT,
                    pr: 1,
                    textAlign: "right",
                    fontSize: 11,
                    color: "var(--dashboard-text-secondary)",
                    transform: "translateY(-7px)",
                  }}
                >
                  {slot.key}
                </Typography>
              ),
            )}
          </Box>

          {days.map((day, dayIndex) => {
            const date = day.format(CALENDAR_DATE_FORMAT);
            const dayLayouts = JobModel.buildDayLayout(
              displayJobs.filter((job) => job.date === date),
            );

            return (
              <Box
                key={date}
                data-day-index={dayIndex}
                sx={{
                  position: "relative",
                  height: DAY_HEIGHT,
                  overflow: "hidden",
                  borderLeft: "1px solid var(--dashboard-border-row)",
                }}
              >
                {CALENDAR_TIME_SLOTS.map((slot, index) => (
                  <Box
                    key={slot.value}
                    aria-label={`New job on ${day.format("dddd")} at ${slot.key}`}
                    onClick={() => onSlotClick(day, String(slot.value))}
                    sx={{
                      height: SLOT_HEIGHT,
                      cursor: "pointer",
                      borderTop:
                        index % 2 === 0
                          ? "1px solid var(--dashboard-border-row)"
                          : "1px dashed var(--dashboard-border-row)",
                      "&:hover": {
                        backgroundColor: "var(--dashboard-background)",
                      },
                    }}
                  />
                ))}

                {dayLayouts.map(({ job, lane, laneCount }) => {
                  const top =
                    (JobModel.timeToMinutes(job.startTime) -
                      CALENDAR_START_HOUR * 60) *
                    MINUTE_HEIGHT;
                  const height = job.durationMinutes * MINUTE_HEIGHT;
                  const isDragging = drag?.job.id === job.id;

                  return (
                    <Box
                      key={job.id}
                      onPointerDown={(event) =>
                        handlePointerDown(event, job, JobDragMode.Move)
                      }
                      onClickCapture={(event) => {
                        if (justDraggedRef.current) event.stopPropagation();
                      }}
                      sx={{
                        position: "absolute",
                        top: Math.max(top, 0) + 1,
                        height: height - 2,
                        left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                        width: `calc(${100 / laneCount}% - 4px)`,
                        cursor: isDragEnabled
                          ? isDragging
                            ? "grabbing"
                            : "grab"
                          : undefined,
                        zIndex: isDragging ? 2 : undefined,
                        opacity: isDragging ? 0.85 : undefined,
                      }}
                    >
                      <JobCard
                        job={job}
                        hasConflict={conflictJobIds.has(job.id)}
                        isCompact={height < CALENDAR_HOUR_HEIGHT}
                        onClick={onJobClick}
                      />
                      {isDragEnabled && (
                        <Box
                          aria-hidden
                          onPointerDown={(event) =>
                            handlePointerDown(event, job, JobDragMode.Resize)
                          }
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 6,
                            cursor: "ns-resize",
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
